import React from 'react';
import { pick } from 'lodash';

import CategorizedDonutPieChart from '../ui/atoms/CategorizedDonutPieChart';
import CategorizedHistogramChart from '../ui/atoms/CategorizedHistogramChart';
import CategorizedPieChart from '../ui/atoms/CategorizedPieChart';
import ConnectedScatterplotChart from '../ui/atoms/ConnectedScatterplotChart';
import GroupedBarChart from '../ui/atoms/GroupedBarChart';
import HorizontalGroupedBarChart from '../ui/atoms/HorizontalGroupedBarChart';
import MultiLineChart from '../ui/atoms/MultiLineChart';
import NumericDonutPieChart from '../ui/atoms/NumericDonutPieChart';
import NumericHistogramChart from '../ui/atoms/NumericHistogramChart';
import NumericPieChart from '../ui/atoms/NumericPieChart';
import RadialLineChart from '../ui/atoms/RadialLineChart';
import RadialOrderedStackedBarChart from '../ui/atoms/RadialOrderedStackedBarChart';
import RadialStackedBarChart from '../ui/atoms/RadialStackedBarChart';
import RegressionScatterplotChart from '../ui/atoms/RegressionScatterplotChart';
import SimpleBarChart from '../ui/atoms/SimpleBarChart';
import SimpleLineChart from '../ui/atoms/SimpleLineChart';
import SimpleHorizontalBarChart from '../ui/atoms/SimpleHorizontalBarChart';
import SimpleNumber from '../ui/atoms/SimpleNumber';
import StackedBarChart from '../ui/atoms/StackedBarChart';
import StackedHorizontalBarChart from '../ui/atoms/StackedHorizontalBarChart';
import StackedToGroupedBarChart from '../ui/atoms/StackedToGroupedBarChart';
import TreemapChart from '../ui/atoms/TreemapChart';

export const ENTRY_DATA_TYPES = {
  NONE: 'none',
  SINGLE: 'single',
  NUMERIC: 'numeric',
  MULTIPLECOLORS: 'multipleColors',
  MULTIPLEVALUES: 'multipleValues',
  MULTIPLECOLORSANDVALUES: 'multipleColorsAndValues',
  SINGLECOLORSANDVALUES: 'singleColorsAndValues',
  properties: {
    NONE: {
      name: 'NONE',
      value: 'none',
      humanValue: 'No named Properties'
    },
    SINGLE: {
      name: 'SINGLE',
      value: 'single',
      humanValue: 'One named Property'
    },
    NUMERIC: {
      name: 'NUMERIC',
      value: 'numeric',
      humanValue: 'One numeric named Property'
    },
    SINGLECOLORSANDVALUES: {
      name: 'SINGLECOLORSANDVALUES',
      value: 'singleColorsAndValues',
      humanValue: 'One numeric value, one color'
    },
    MULTIPLECOLORS: {
      name: 'MULTIPLECOLORS',
      value: 'multipleColors',
      humanValue: 'One numeric value, multiple colors'
    },
    MULTIPLEVALUES: {
      name: 'MULTIPLEVALUES',
      value: 'multipleValues',
      humanValue: 'Multiple numeric value, one color per value'
    },
    MULTIPLECOLORSANDVALUES: {
      name: 'MULTIPLECOLORSANDVALUES',
      value: 'multipleColorsAndValues',
      humanValue: 'Multiple numeric values, multiple colors per value'
    }
  }
};

export const GRAPH_ELEMENTS = {
  BARCHART: <SimpleBarChart />,
  CATEGORIZEDDONUTCHART: <CategorizedDonutPieChart />,
  CATEGORIZEDHISTOGRAMCHART: <CategorizedHistogramChart />,
  CATEGORIZEDPIECHART: <CategorizedPieChart />,
  CONNECTEDSCATTERPLOTCHART: <ConnectedScatterplotChart />,
  GROUPEDBAR: <GroupedBarChart />,
  HORIZONTALBAR: <SimpleHorizontalBarChart />,
  HORIZONTALGROUPEDBAR: <HorizontalGroupedBarChart />,
  LINECHART: <SimpleLineChart />,
  MULTILINECHART: <MultiLineChart />,
  NUMERICDONUTCHART: <NumericDonutPieChart />,
  NUMERICHISTOGRAMCHART: <NumericHistogramChart />,
  NUMERICPIECHART: <NumericPieChart />,
  RADIALLINECHART: <RadialLineChart />,
  RADIALORDEREDSTACKEDBARCHART: <RadialOrderedStackedBarChart />,
  RADIALSTACKEDBARCHART: <RadialStackedBarChart />,
  REGRESSIONSCATTERPLOTCHART: <RegressionScatterplotChart />,
  SIMPLENUMBER: <SimpleNumber />,
  TREEMAPCHART: <TreemapChart />,
  STACKEDBAR: <StackedBarChart />,
  STACKEDHORIZONTALBAR: <StackedHorizontalBarChart />,
  STACKEDTOGROUPEDBAR: <StackedToGroupedBarChart />,
  properties: {
    LINECHART: {
      name: 'LINECHART',
      value: <SimpleLineChart />,
      humanValue: 'Line Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES
    },
    MULTILINECHART: {
      name: 'MULTILINECHART',
      value: <MultiLineChart />,
      humanValue: 'Multiple Line Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLEVALUES
    },
    CONNECTEDSCATTERPLOTCHART: {
      name: 'CONNECTEDSCATTERPLOTCHART',
      value: <ConnectedScatterplotChart />,
      humanValue: 'Connected Scatterplot Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES
    },
    REGRESSIONSCATTERPLOTCHART: {
      name: 'REGRESSIONSCATTERPLOTCHART',
      value: <RegressionScatterplotChart />,
      humanValue: 'Regression Scatterplot Chart',
      dataNamedType: ENTRY_DATA_TYPES.NUMERIC,
      dataNumericType: ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES
    },
    BARCHART: {
      name: 'BARCHART',
      value: <SimpleBarChart />,
      humanValue: 'Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    NUMERICPIECHART: {
      name: 'NUMERICPIECHART',
      value: <NumericPieChart />,
      humanValue: 'Numeric Pie Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    CATEGORIZEDPIECHART: {
      name: 'CATEGORIZEDPIECHART',
      value: <CategorizedPieChart />,
      humanValue: 'Categorized Pie Chart',
      dataNamedType: ENTRY_DATA_TYPES.NONE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    NUMERICDONUTCHART: {
      name: 'NUMERICDONUTCHART',
      value: <NumericDonutPieChart />,
      humanValue: 'Numeric Donut Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    CATEGORIZEDDONUTCHART: {
      name: 'CATEGORIZEDDONUTCHART',
      value: <CategorizedDonutPieChart />,
      humanValue: 'Categorized Donut Chart',
      dataNamedType: ENTRY_DATA_TYPES.NONE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    RADIALLINECHART: {
      name: 'RADIALLINECHART',
      value: <RadialLineChart />,
      humanValue: 'Radial Line Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES
    },
    RADIALSTACKEDBARCHART: {
      name: 'RADIALSTACKEDBARCHART',
      value: <RadialStackedBarChart />,
      humanValue: 'Radial Stacked Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    RADIALORDEREDSTACKEDBARCHART: {
      name: 'RADIALORDEREDSTACKEDBARCHART',
      value: <RadialOrderedStackedBarChart />,
      humanValue: 'Radial Ordered Stacked Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    TREEMAPCHART: {
      name: 'TREEMAPCHART',
      value: <TreemapChart />,
      humanValue: 'Treemap Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    SIMPLENUMBER: {
      name: 'SIMPLENUMBER',
      value: <SimpleNumber />,
      humanValue: 'Simple Number',
      dataNamedType: ENTRY_DATA_TYPES.NONE,
      dataNumericType: ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES
    },
    HORIZONTALBAR: {
      name: 'HORIZONTALBAR',
      value: <SimpleHorizontalBarChart />,
      humanValue: 'Horizontal Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    STACKEDBAR: {
      name: 'STACKEDBAR',
      value: <StackedBarChart />,
      humanValue: 'Stacked Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    STACKEDHORIZONTALBAR: {
      name: 'STACKEDHORIZONTALBAR',
      value: <StackedHorizontalBarChart />,
      humanValue: 'Stacked Horizontal Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    GROUPEDBAR: {
      name: 'GROUPEDBAR',
      value: <GroupedBarChart />,
      humanValue: 'Grouped Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    HORIZONTALGROUPEDBAR: {
      name: 'HORIZONTALGROUPEDBAR',
      value: <HorizontalGroupedBarChart />,
      humanValue: 'Horizontal Grouped Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    STACKEDTOGROUPEDBAR: {
      name: 'STACKEDTOGROUPEDBAR',
      value: <StackedToGroupedBarChart />,
      humanValue: 'Stacked To Grouped Bar Chart',
      dataNamedType: ENTRY_DATA_TYPES.SINGLE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES
    },
    NUMERICHISTOGRAMCHART: {
      name: 'NUMERICHISTOGRAMCHART',
      value: <NumericHistogramChart />,
      humanValue: 'Numeric Histogram Chart',
      dataNamedType: ENTRY_DATA_TYPES.NONE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    },
    CATEGORIZEDHISTOGRAMCHART: {
      name: 'CATEGORIZEDHISTOGRAMCHART',
      value: <CategorizedHistogramChart />,
      humanValue: 'Categorized Histogram Chart',
      dataNamedType: ENTRY_DATA_TYPES.NONE,
      dataNumericType: ENTRY_DATA_TYPES.MULTIPLECOLORS
    }
  }
};

export const CIRCLE_FAMILY = pick(GRAPH_ELEMENTS, [
  'NUMERICPIECHART',
  'CATEGORIZEDPIECHART',
  'NUMERICDONUTCHART',
  'CATEGORIZEDDONUTCHART',
  'RADIALLINECHART',
  'RADIALSTACKEDBARCHART',
  'RADIALORDEREDSTACKEDBARCHART'
]);

export const BAR_FAMILY = pick(GRAPH_ELEMENTS, [
  'BARCHART',
  'HORIZONTALBAR',
  'NUMERICHISTOGRAMCHART',
  'CATEGORIZEDHISTOGRAMCHART',
  'STACKEDBAR',
  'STACKEDHORIZONTALBAR',
  'GROUPEDBAR',
  'HORIZONTALGROUPEDBAR',
  'STACKEDTOGROUPEDBAR'
]);

export const LINE_FAMILY = pick(GRAPH_ELEMENTS, [
  'LINECHART',
  'MULTILINECHART',
  'CONNECTEDSCATTERPLOTCHART',
  'REGRESSIONSCATTERPLOTCHART'
]);

export const SPECIAL_FAMILY = pick(GRAPH_ELEMENTS, ['TREEMAPCHART', 'SIMPLENUMBER']);

export const GRAPH_FAMILIES = [CIRCLE_FAMILY, BAR_FAMILY, LINE_FAMILY, SPECIAL_FAMILY];

export const X_AXIS_TEXT_ANGLE_VARIABLE = [
  'BARCHART',
  'NUMERICHISTOGRAMCHART',
  'CATEGORIZEDHISTOGRAMCHART',
  'STACKEDBAR',
  'GROUPEDBAR',
  'STACKEDTOGROUPEDBAR',
  'LINECHART',
  'MULTILINECHART',
  'CONNECTEDSCATTERPLOTCHART',
  'REGRESSIONSCATTERPLOTCHART'
];

export const TEXT_SIZE_VARIBLE = [
  'BARCHART',
  'CATEGORIZEDDONUTCHART',
  'CATEGORIZEDHISTOGRAMCHART',
  'CATEGORIZEDPIECHART',
  'CONNECTEDSCATTERPLOTCHART',
  'GROUPEDBAR',
  'HORIZONTALBAR',
  'HORIZONTALGROUPEDBAR',
  'LINECHART',
  'MULTILINECHART',
  'NUMERICDONUTCHART',
  'NUMERICHISTOGRAMCHART',
  'NUMERICPIECHART',
  'RADIALLINECHART',
  'RADIALORDEREDSTACKEDBARCHART',
  'RADIALSTACKEDBARCHART',
  'REGRESSIONSCATTERPLOTCHART',
  'TREEMAPCHART',
  'STACKEDBAR',
  'STACKEDHORIZONTALBAR',
  'STACKEDTOGROUPEDBAR'
];
